import apiClient from './api';
import type { Stock, CreateStockRequest, UpdateStockRequest, StockAvailability } from '../types/stock';
import type { PagedResponse } from '../types/common';

export const stockService = {
  // Search stocks with optional name filter and pagination
  search: async (
    companyId: number,
    params?: { name?: string; includeArchived?: boolean; page?: number; size?: number },
  ): Promise<PagedResponse<Stock>> => {
    const response = await apiClient.post<PagedResponse<Stock>>('/stocks/search', {
      companyId,
      name: params?.name || undefined,
      includeArchived: params?.includeArchived ?? false,
      page: params?.page ?? 0,
      size: params?.size ?? 20,
      sortBy: 'name',
      sortDir: 'asc',
    });
    return response.data;
  },

  // Get all stocks for a company
  getAllByCompany: async (companyId: number): Promise<Stock[]> => {
    const response = await apiClient.get('/stocks', {
      params: { companyId },
    });
    const data = response.data;

    // Ensure we always return an array
    if (!Array.isArray(data)) {
      console.warn('API returned non-array data for stocks:', data);
      return [];
    }

    return data;
  },

  // Get stock by ID
  getById: async (id: number): Promise<Stock> => {
    const response = await apiClient.get(`/stocks/${id}`);
    return response.data;
  },

  // Create new stock
  create: async (stockData: CreateStockRequest): Promise<Stock> => {
    const response = await apiClient.post('/stocks', stockData);
    return response.data;
  },

  // Update stock
  update: async (id: number, stockData: UpdateStockRequest): Promise<Stock> => {
    const response = await apiClient.put(`/stocks/${id}`, stockData);
    return response.data;
  },

  // Soft delete stock
  delete: async (id: number): Promise<void> => {
    await apiClient.delete(`/stocks/${id}`);
  },

  // Hard delete stock
  hardDelete: async (id: number): Promise<void> => {
    await apiClient.delete(`/stocks/${id}/hard`);
  },

  // Restore an archived stock
  restore: async (id: number): Promise<void> => {
    await apiClient.post(`/stocks/${id}/restore`);
  },

  // Get product availability on stocks
  getAvailability: async (companyId: number, stockIds: number[]): Promise<StockAvailability[]> => {
    const response = await apiClient.get(`/stocks/companies/${companyId}/availability`, {
      params: {
        stockIds
      },
    });
    return response.data;
  },
};
